/**
 * CSV Deal Import — vendor-facing bulk uploader.
 *
 * Route:   /food-pulse/vendor/import
 * Backend: POST /api/imports/deals  (multipart file OR csv_text)
 *          GET  /api/imports/templates/deals
 *
 * Flow:
 *   1. Download the template — pre-filled with column headers + one
 *      sample row so vendors can just paste new rows in Sheets/Excel
 *   2. Pick a file (or paste CSV text)
 *   3. Submit → server returns { inserted, failed, errors, inserted_slugs }
 *   4. Show the per-row report + quick "View my deals" CTA
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Download, FileText, CheckCircle2, XCircle, Loader2,
} from "lucide-react";
import { http, BACKEND_ORIGIN } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function CsvDealImport() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [file,   setFile]   = useState(null);
  const [pasted, setPasted] = useState("");
  const [busy,   setBusy]   = useState(false);
  const [report, setReport] = useState(null);

  if (!authLoading && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "#FBF7F0" }}>
        <div className="max-w-md text-center bg-white rounded-2xl border p-8"
             style={{ borderColor: "#C79A3F" }} data-testid="import-signin-required">
          <FileText className="w-12 h-12 mx-auto opacity-40 mb-3" style={{ color: "#8B5E10" }} />
          <h1 className="font-display text-xl mb-2">Sign in to bulk-import deals</h1>
          <p className="text-sm opacity-70 mb-6">
            You'll need to be signed in so we can attribute the deals to your vendor account.
          </p>
          <Link to="/login?next=/food-pulse/vendor/import"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-white font-semibold"
                style={{ background: "#8B5E10" }}>Sign in →</Link>
        </div>
      </div>
    );
  }

  async function submit() {
    if (!file && !pasted.trim()) {
      toast.error("Pick a CSV file or paste CSV text first");
      return;
    }
    setBusy(true);
    setReport(null);
    try {
      const fd = new FormData();
      if (file)  fd.append("file", file);
      else       fd.append("csv_text", pasted);
      const res = await http.post("/imports/deals", fd);
      setReport(res.data);
      if (res.data.inserted > 0) {
        toast.success(`Imported ${res.data.inserted} deal${res.data.inserted === 1 ? "" : "s"}`);
      } else {
        toast.error("Nothing imported — see the report below");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed. Check the file and try again.");
    } finally {
      setBusy(false);
    }
  }

  const templateUrl = `${BACKEND_ORIGIN}/api/imports/templates/deals`;

  return (
    <div className="min-h-screen" style={{ background: "#FBF7F0" }} data-testid="csv-import-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/food-pulse/vendor"
              className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-3"
              data-testid="import-back-link">
          <ArrowLeft className="w-3 h-3" /> Back to vendor dashboard
        </Link>

        <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "#B25D38" }}>
          Vendor tools · Bulk import
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-3" style={{ color: "#1A1613" }}>
          Import deals from a CSV
        </h1>
        <p className="opacity-80 text-sm max-w-2xl mb-8">
          Upload a spreadsheet of deals and publish them all at once. Perfect for
          end-of-day surplus, weekly farm-stand price sheets, or migrating from
          another platform.
        </p>

        {/* Step 1 — Template */}
        <section className="rounded-2xl border bg-white p-5 mb-6"
                 style={{ borderColor: "#E9D8B4" }}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-none"
                 style={{ background: "#F0E2C7", color: "#8B5E10" }}>1</div>
            <div className="flex-1">
              <div className="font-display text-lg mb-1">Grab the template</div>
              <p className="text-sm opacity-80 mb-3">
                One header row + one filled-out sample. Open it in Google Sheets or
                Excel, add your rows, save as CSV, then come back here.
              </p>
              <a href={templateUrl} download
                 className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border hover:shadow-sm transition-shadow"
                 style={{ background: "#F0E2C7", borderColor: "#C79A3F", color: "#8B5E10" }}
                 data-testid="import-template-download">
                <Download className="w-4 h-4" /> Download template.csv
              </a>
              <details className="mt-4 text-xs opacity-80">
                <summary className="cursor-pointer">Column reference</summary>
                <ul className="mt-2 space-y-1 pl-4 list-disc">
                  <li><code className="font-mono">kind</code> — required. one of <em>offer</em>, <em>coupon</em>, <em>surplus</em></li>
                  <li><code className="font-mono">title</code> — required, 3+ characters</li>
                  <li><code className="font-mono">description</code>, <code className="font-mono">vendor_name</code>, <code className="font-mono">vendor_slug</code></li>
                  <li><code className="font-mono">city</code>, <code className="font-mono">state</code>, <code className="font-mono">zip</code></li>
                  <li><code className="font-mono">discount_pct</code> — integer 0–100</li>
                  <li><code className="font-mono">original_price</code>, <code className="font-mono">deal_price</code> — decimals</li>
                  <li><code className="font-mono">quantity_available</code>, <code className="font-mono">unit</code>, <code className="font-mono">pickup_location</code></li>
                  <li><code className="font-mono">starts_at</code>, <code className="font-mono">ends_at</code> — ISO 8601 (optional)</li>
                  <li><code className="font-mono">tags</code> — semicolon-separated (e.g. <em>produce;organic</em>)</li>
                </ul>
              </details>
            </div>
          </div>
        </section>

        {/* Step 2 — Upload */}
        <section className="rounded-2xl border bg-white p-5 mb-6"
                 style={{ borderColor: "#E9D8B4" }}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-none"
                 style={{ background: "#F0E2C7", color: "#8B5E10" }}>2</div>
            <div className="flex-1">
              <div className="font-display text-lg mb-1">Upload your CSV</div>
              <label className="mt-3 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 cursor-pointer hover:bg-[#FBF7F0]"
                     style={{ borderColor: "#C79A3F" }}
                     data-testid="import-file-drop">
                <input type="file"
                       accept=".csv,text/csv"
                       onChange={(e) => { setFile(e.target.files?.[0] || null); setPasted(""); }}
                       className="hidden"
                       data-testid="import-file-input" />
                <Upload className="w-5 h-5 opacity-60" />
                <span className="text-sm">
                  {file ? file.name : "Click to pick a CSV file (or drop it here)"}
                </span>
              </label>

              <details className="mt-4 text-xs">
                <summary className="cursor-pointer opacity-70">Or paste CSV text</summary>
                <textarea value={pasted}
                          onChange={(e) => { setPasted(e.target.value); setFile(null); }}
                          rows={6}
                          placeholder="Paste the CSV rows here — must include a header row"
                          className="mt-2 w-full text-xs font-mono border rounded-lg p-2"
                          style={{ borderColor: "#E9D8B4" }}
                          data-testid="import-csv-text" />
              </details>

              <button onClick={submit} disabled={busy || (!file && !pasted.trim())}
                      className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                      style={{ background: "#8B5E10" }}
                      data-testid="import-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {busy ? "Importing…" : "Import deals"}
              </button>
            </div>
          </div>
        </section>

        {/* Step 3 — Results */}
        {report && (
          <section className="rounded-2xl border bg-white p-5"
                   style={{ borderColor: "#E9D8B4" }}
                   data-testid="import-report">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg">Import report</div>
              <div className="text-xs opacity-70">
                {report.inserted} imported · {report.failed} skipped
              </div>
            </div>
            {report.inserted > 0 && (
              <div className="rounded-lg p-3 mb-3 text-sm flex items-center gap-2"
                   style={{ background: "#E7F5E9", color: "#0F5B24" }}
                   data-testid="import-report-success">
                <CheckCircle2 className="w-4 h-4" />
                {report.inserted} deal{report.inserted === 1 ? "" : "s"} published successfully.
                {report.inserted_slugs?.length > 0 && (
                  <button onClick={() => navigate("/food-pulse/deals")}
                          className="ml-auto underline font-semibold text-xs">
                    View my deals →
                  </button>
                )}
              </div>
            )}
            {report.failed > 0 && (
              <div className="mb-2">
                <div className="text-xs font-mono uppercase tracking-widest mb-1 opacity-60">
                  Rows that were skipped
                </div>
                <ul className="text-sm divide-y" style={{ borderColor: "#F3E7CB" }}>
                  {report.errors.map((err) => (
                    <li key={err.row} className="py-2 flex items-start gap-2"
                        data-testid={`import-error-${err.row}`}>
                      <XCircle className="w-4 h-4 mt-0.5 flex-none" style={{ color: "#B23838" }} />
                      <div>
                        <span className="font-mono text-xs opacity-70">Row {err.row}</span>
                        <div className="text-sm">{err.message}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
